import { FilterMethodBuilder } from "@/utilities/system/helpers/FilterMethodBuilder";
import { UserPreference } from "@/models/system/user/UserPreference";
import { useSystemStore } from "@/stores/system/SystemStore";
import {TableType} from "@/utilities/system/enums/Enums";
import {Tab} from "@bryntum/grid";
import {isDate} from "moment";

class FilterService {
    filterBuilder: FilterMethodBuilder;

    constructor() {
        this.filterBuilder = new FilterMethodBuilder();
    }

    /*
        returns a method that the grid can filter by
        optionally can be used to return a method to filter with tmp-filter-selection
    */
    buildFilter(tableName:string, useTmpFilters:boolean = false) {
        const systemStore = useSystemStore()

        const filters = useTmpFilters
            ? systemStore.tmpPreferences.tmpUserFilters
            : systemStore.userPreferences.userFilters;
        const filterMethodDict = this.getFilterMethodDict(tableName, filters, useTmpFilters);

        // return userPreference-Filter as a method for bryntums filterBy-Method
        return (record:any) => {
            // convert {<propName>:[propFilterMethods], <propName>:[propFilterMethods], ...} to [[propFilterMethods], [propFilterMethods], ...]
            const methodList:any = [];
            Object.keys(filterMethodDict).forEach((k:any) => methodList.push(filterMethodDict[k]));

            let result = true;

            methodList.forEach((propMethodList:any) => {
                let propFilterResult = false;

                // concatinate multiple filters for the same property via '||'
                propMethodList.forEach((method:any) => {
                    propFilterResult = propFilterResult || method(record);
                });

                // concatinate propFilterResult with other propFilterResults via '&&'
                result = result && propFilterResult;
            });

            return result;
        };
    }


    /* ---- PRIVATE ---- */


    /*
        returns a dict for all properties to filter (key == propertyName and value == arrayOfFolterMethods)
    */
    private getFilterMethodDict (tableName:string, filters:Array<UserPreference>, useTmpFilters:boolean = false): any {
        // filter for only table-relevant filters and also ignore filters for hidden columns
        const tableFilters = filters.filter((f:UserPreference) => f.tableName === tableName && !this.isForHiddenColumn(f, useTmpFilters));

        switch(tableName) {
            case TableType[TableType.form]:
                return this.getFormMethods(tableFilters);
                break;
            default: {
                console.log("[FilterService] [getFilterMethodDict]: unknown table");
                return {};
                break;
            }
        }
    }

    /*
        init result as dict where 'key == propName' && 'value == array'
    */
    private initFilterList(filters:Array<UserPreference>){
        return filters.reduce((initResult:any , filter:UserPreference)=>{
            const attr = filter.attributeName;

            if (initResult[attr] === undefined) initResult[attr] = [];

            return initResult;
        }, {});
    }

    /*
        translate from attributeName to col-field-id and return if the filter is for a hidden column
    */
    private isForHiddenColumn(filter:UserPreference, useTmpFilters:boolean = false) {
        let attribute = "";

        switch(filter.attributeName){
            // add column names if they dont match the prop name here
            default:{
                attribute = filter.attributeName;
                break;
            }
        }

        const systemStore = useSystemStore();

        const colStore = useTmpFilters
            ? systemStore.tmpPreferences.tmpUserHiddenColumns
            : systemStore.userPreferences.userHiddenColumns;

        return colStore.some((hc:UserPreference) => hc.attributeName === attribute);
    }


    /* TABLE METHODS */


    // returns array of filter-methods per property to apply to the record
    private getFormMethods(filters:Array<UserPreference>):any{
        const result = this.initFilterList(filters);

        // fill with correct filter-methods
        filters.forEach((f:UserPreference) => {
            // CAUTION MAGIC STRING MAGIC (when filtering for Dates and checking for > or < we name the attribute with START or END at the end...) I AM SORRY
            if (f.attributeName.endsWith("START")) {
                const convertedAttrValue = new Date(f.attributeValue);
                if(isDate(convertedAttrValue)) result[f.attributeName].push(this.filterBuilder.form.filterStartDate(f.attributeName.replace("START", ""), convertedAttrValue));

                return;
            }
            // CAUTION MAGIC STRING MAGIC (when filtering for Dates and checking for > or < we name the attribute with START or END at the end...) I AM SORRY
            if (f.attributeName.endsWith("END")) {
                const convertedAttrValue = new Date(f.attributeValue);
                if(isDate(convertedAttrValue)) result[f.attributeName].push(this.filterBuilder.form.filterEndDate(f.attributeName.replace("END", ""), convertedAttrValue));

                return;
            }
            if(f.attributeName === "isOpen"){
                const convertedAttrValue = f.attributeValue === "true";
                result[f.attributeName].push(this.filterBuilder.form.filterIsOpen(convertedAttrValue));
                return;
            }
            if(f.attributeName === "type"){
                const convertedAttrValue = parseInt(f.attributeValue);
                if(!isNaN(convertedAttrValue) && convertedAttrValue >=0 && convertedAttrValue<=5) result[f.attributeName].push(this.filterBuilder.form.filterType(convertedAttrValue));
                return;
            }

            console.log("attribute not known, skipping filter");
            return;
        });

        return result;
    }
}

export default new FilterService();
